// zod/mini: the same core and checks as zod, as tree-shakable functions (the app bundles these schemas).
// The server's `import "zod"` installs zod's English messages globally, so its errors read the same.
import { z } from "zod/mini";
import type { AscentPhoto } from "./photos";

export const ACTIVITIES = [
  "senderismo",
  "alpinismo",
  "esqui_montana",
  "raquetas",
  "escalada",
  "via_ferrata",
] as const;
export const SAC_SCALE = ["T1", "T2", "T3", "T4", "T5", "T6"] as const;
export const ALPINE_GRADES = ["F", "PD-", "PD", "PD+", "AD", "D", "TD"] as const;
export const SEASONS = ["invierno", "primavera", "verano", "otono"] as const;
export const PEAK_SOURCES = ["osm", "wikidata", "custom"] as const;

export type Activity = (typeof ACTIVITIES)[number];
export type SacScale = (typeof SAC_SCALE)[number];
export type AlpineGrade = (typeof ALPINE_GRADES)[number];
export type Season = (typeof SEASONS)[number];

/** Combined difficulty rank used for sorting (0 = unknown). */
export function difficultyRank(sac: SacScale | null | undefined, alpine: AlpineGrade | null | undefined): number {
  const s = sac ? SAC_SCALE.indexOf(sac) + 1 : 0;
  const a = alpine ? ALPINE_GRADES.indexOf(alpine) + 3 : 0; // F ≈ T3+, TD above T6
  return Math.max(s, a);
}

export const peakId = z.string().check(z.regex(/^(?:[nwr]\d{1,12}|Q\d{1,12}|c-[0-9a-f-]{36})$/, "peak id"));
const isoDate = z.string().check(z.regex(/^\d{4}-\d{2}-\d{2}$/));
const hhmm = z.string().check(z.regex(/^([01]\d|2[0-3]):[0-5]\d$/));
const text = (max: number) => z.string().check(z.trim(), z.maxLength(max));
const optText = (max: number) => z.pipe(z.nullish(text(max)), z.transform((v) => (v ? v : null)));
/** Nullish → null (zod's `.nullish().transform((v) => v ?? null)`). */
const orNull = <T extends z.core.SomeType>(schema: T) => z.pipe(z.nullish(schema), z.transform((v: z.output<T> | null | undefined) => v ?? null));
const optInt = (min: number, max: number) => orNull(z.number().check(z.int(), z.gte(min), z.lte(max)));

export const altNamesSchema = z.partial(
  z.object({
    es: z.optional(z.string()),
    fr: z.optional(z.string()),
    eu: z.optional(z.string()),
    an: z.optional(z.string()),
    ca: z.optional(z.string()),
    oc: z.optional(z.string()),
    en: z.optional(z.string()),
    /** Original local-language name (OSM `name`) when the display name is Spanish. Search only. */
    local: z.optional(z.string()),
  }),
);
export type AltNames = z.infer<typeof altNamesSchema>;
/** Alternative-name languages shown in the UI (Catalan/Occitan/English/local are search-only). */
export const DISPLAY_NAME_LANGS = ["es", "fr", "eu", "an"] as const;

export const peakSchema = z.object({
  id: peakId,
  name: z.string(),
  altNames: z.nullable(altNamesSchema),
  elevation: z.nullable(z.number()),
  prominence: z.nullable(z.number()),
  lat: z.number(),
  lon: z.number(),
  massif: z.nullable(z.string()),
  country: z.nullable(z.string()),
  source: z.enum(PEAK_SOURCES),
  osmType: z.nullable(z.enum(["node", "way", "relation"])),
  osmId: z.nullable(z.number()),
  wikidataId: z.nullable(z.string()),
  wikipediaUrl: z.nullable(z.string()),
  imageUrl: z.nullable(z.string()),
  imageAttribution: z.nullable(z.string()),
  inCatalog: z.boolean(),
  lastSyncedAt: z.nullable(z.string()),
});
export type Peak = z.infer<typeof peakSchema>;

/** Create/edit a custom peak (tapping the map). */
export const customPeakInput = z.object({
  name: text(120).check(z.minLength(1)),
  elevation: orNull(z.number().check(z.gte(-500), z.lte(9000))),
  lat: z.number().check(z.gte(-90), z.lte(90)),
  lon: z.number().check(z.gte(-180), z.lte(180)),
  massif: optText(120),
  country: optText(2),
});
export type CustomPeakInput = z.input<typeof customPeakInput>;

/** A candidate coming from live search (Overpass / Nominatim / Wikidata) to be upserted. */
export const externalPeakCandidate = z.object({
  name: text(160).check(z.minLength(1)),
  lat: z.number().check(z.gte(-90), z.lte(90)),
  lon: z.number().check(z.gte(-180), z.lte(180)),
  elevation: orNull(z.number().check(z.gte(-500), z.lte(9000))),
  osmType: orNull(z.enum(["node", "way", "relation"])),
  osmId: orNull(z.number().check(z.int(), z.gt(0))),
  wikidataId: orNull(z.string().check(z.regex(/^Q\d+$/))),
  country: optText(2),
  /** Local-language name when `name` is the Spanish one (kept as a search alias). */
  localName: optText(160),
  source: z.enum(["osm", "wikidata"]),
});
export type ExternalPeakCandidate = z.infer<typeof externalPeakCandidate>;

export const ascentInput = z
  .object({
    date: isoDate,
    startTime: orNull(hhmm),
    durationMin: optInt(0, 60 * 24 * 14),
    timeToSummitMin: optInt(0, 60 * 24 * 14),
    elevationGain: optInt(0, 20000),
    distanceKm: orNull(z.number().check(z.gte(0), z.lte(1000))),
    activity: z.enum(ACTIVITIES),
    sacScale: orNull(z.enum(SAC_SCALE)),
    alpineGrade: orNull(z.enum(ALPINE_GRADES)),
    difficultyNotes: optText(200),
    routeName: optText(200),
    startPoint: optText(200),
    winter: z._default(z.boolean(), false),
    conditions: optText(1000),
    companions: optText(500),
    rating: optInt(1, 5),
    notes: optText(10000),
    externalUrl: orNull(z.url({ protocol: /^https?$/ }).check(z.maxLength(500))),
    /** "Solo para mí": out of everything others see (feeds, boards, their view of the passport). */
    private: z._default(z.boolean(), false),
    peakIds: z.array(peakId).check(z.minLength(1), z.maxLength(30)),
  })
  .check(z.refine((a) => new Set(a.peakIds).size === a.peakIds.length, { message: "duplicate peaks", path: ["peakIds"] }));
export type AscentInput = z.input<typeof ascentInput>;
export type AscentData = z.output<typeof ascentInput>;

export type Ascent = AscentData & {
  id: string;
  year: number;
  gpxKey: string | null;
  createdAt: string;
  updatedAt: string;
  /** When the server first stored it, or its new date (late logging: see src/shared/stats.ts). */
  countedAt: string | null;
  /** At most MAX_ASCENT_PHOTOS (src/shared/photos.ts). The server always sends it; ascents persisted on a
   * device by an older version lack it, so the app reads `photos ?? []`. */
  photos?: AscentPhoto[];
  /** The group ascent it belongs to (docs/archive/PLAN-GROUPS.md §2): the same id on every member's copy. */
  groupId: string | null;
};

export const listInput = z.object({
  name: text(120).check(z.minLength(1)),
  description: optText(2000),
  position: z.optional(z.number().check(z.int(), z.gte(0), z.lte(100000))),
});
export type ListInput = z.input<typeof listInput>;

export const listItemInput = z.object({
  priority: z._default(z.number().check(z.int(), z.gte(1), z.lte(3)), 2),
  targetSeason: orNull(z.enum(SEASONS)),
  targetYear: optInt(1900, 2200),
  plannedRoute: optText(500),
  notes: optText(5000),
  position: z.optional(z.number().check(z.int(), z.gte(0), z.lte(100000))),
});
export type ListItemInput = z.input<typeof listItemInput>;

/**
 * The Bucket list: every account has one, under this fixed id, for the peaks the user most wants to
 * climb. It can't be renamed or deleted, a peak climbed can't go in, and a peak leaves it as soon as
 * an ascent of it is logged. The server creates its row on the first write; until then the app
 * shows it empty.
 */
export const BUCKET_LIST_ID = "bucket";

export const bulkItemsInput = z.object({ peakIds: z.array(peakId).check(z.minLength(1), z.maxLength(2000)) });
export const reorderInput = z.object({ ids: z.array(z.string().check(z.minLength(1), z.maxLength(64))).check(z.minLength(1), z.maxLength(500)) });
/** Stamps pinned to the profile, in display order. */
export const MAX_PINS = 3;
export const pinsInput = z
  .object({ peakIds: z.array(peakId).check(z.maxLength(MAX_PINS)) })
  .check(z.refine((p) => new Set(p.peakIds).size === p.peakIds.length, { message: "duplicate peaks", path: ["peakIds"] }));
export type PinsInput = z.input<typeof pinsInput>;

/**
 * Terms and privacy policy in force: accepting them is recorded per account with this version, and a new
 * version asks everyone to accept again. Bump it whenever /terminos or /privacidad change in substance.
 */
export const TERMS_VERSION = "2026-10-22";
/** Minimum age to create an account (Spain's age of digital consent, LOPDGDD art. 7). */
export const MIN_AGE = 14;
export const SEXES = ["female", "male", "other", "undisclosed"] as const;
export type Sex = (typeof SEXES)[number];

/** Age reached this year: the birth year is all that is stored. */
export const ageIn = (birthYear: number, year = new Date().getFullYear()) => year - birthYear;
/** The latest birth year allowed to sign up, and the earliest one offered. */
export const maxBirthYear = (year = new Date().getFullYear()) => year - MIN_AGE;
export const MIN_BIRTH_YEAR = 1920;

/** The profile the onboarding asks for (and Ajustes edits): everything but the picture is required. */
export const profileInput = z.object({
  name: z.string().check(z.trim(), z.minLength(1), z.maxLength(60)),
  birthYear: z.number().check(
    z.int(),
    z.gte(MIN_BIRTH_YEAR),
    z.refine((y) => y <= maxBirthYear(), { message: "too young" }),
  ),
  sex: z.enum(SEXES),
  homeBase: z.string().check(z.trim(), z.minLength(1), z.maxLength(80)),
});
export type ProfileInput = z.input<typeof profileInput>;

export const termsInput = z.object({ version: z.string().check(z.minLength(1), z.maxLength(32)) });

/** GET /api/profile */
export type Profile = {
  userId: string;
  avatarUrl: string | null;
  name: string;
  birthYear: number | null;
  sex: Sex | null;
  homeBase: string | null;
  termsVersion: string | null;
  termsAcceptedAt: string | null;
};

/** What the onboarding still has to ask: the profile, and/or the terms in force. Part of GET /api/me. */
export type OnboardingNeeds = { profile: boolean; terms: boolean };

export type ListItem = z.output<typeof listItemInput> & {
  listId: string;
  peakId: string;
  position: number;
  addedAt: string;
  /** Derived from ascents, never stored. */
  firstAscentDate: string | null;
};

export type BucketList = {
  id: string;
  name: string;
  description: string | null;
  kind: "custom" | "curated";
  sourceName: string | null;
  sourceUrl: string | null;
  licence: string | null;
  approximate: boolean;
  position: number;
  createdAt: string;
  updatedAt: string;
  items: ListItem[];
};

/** Live-search result row (normalised across Overpass / Nominatim / Wikidata). */
export type ExternalResult = ExternalPeakCandidate & {
  label: string;
  description: string | null;
  /** Existing D1 peak id if already known (by external ids). */
  existingId: string | null;
};

export type ApiError = { error: string; details?: unknown };
